<template>
   <b-modal
      @hidden="hidden"
      @hide="changeState"
      :id="`${expenseType}-add-vendor-modal`"
      size="lg"
      centered
      ref="addVendorModal"
      header-class="font-weight-normal mx-4"
      body-class="mx-4 body-class"
      dialog-class="modal-add-vendor"
      no-close-on-backdrop
      no-close-on-esc
      hide-footer
   >
      <template #modal-title>
         <h5
            style="
               font-weight: 600;
               margin-bottom: 10px;
               margin-left: 0;
               color: black;
               font-size: 1.15rem;
            "
         >
            {{ addManageLabel }}
         </h5>
      </template>
      <hr class="full-width-line" />
      <div v-if="showFlowToggle" style="margin-bottom: 25px">
         <b-button-group size="md" class="toggle-switch">
            <b-button
               @click="selected = 'import'"
               style="
                  border-radius: 0.65rem;
                  border-style: none;
                  color: #1560a9 !important;
                  font-weight: 400;
               "
               :style="`${
                  selected === 'import' ? 'background-color: white' : 'background-color: #eeeeee'
               }`"
            >
               Import {{ entityLabelPlural }}
            </b-button>
            <b-button
               class="text-primary"
               @click="selected = 'manual'"
               style="
                  border-radius: 0.65rem;
                  border-style: none;
                  color: #1560a9 !important;
                  font-weight: 400;
               "
               :style="`${
                  selected === 'manual' ? 'background-color: white' : 'background-color: #eeeeee'
               }`"
            >
               Manually Added {{ entityLabelPlural }}
            </b-button>
         </b-button-group>
      </div>

      <AddVendorImportFlow
         v-if="expenseType !== 'contractors' && canImportEmployees"
         v-show="selected === 'import'"
         ref="importFlow"
         :expense-type="expenseType"
         @modal-state-change="importModalState = $event"
         @finish="closeModal"
      />
      <AddVendorManualFlow
         v-show="selected === 'manual'"
         ref="manualFlow"
         :expense-type="expenseType"
      />
   </b-modal>
</template>

<script>
import AddVendorImportFlow from './AddVendorImportFlow.vue';
import AddVendorManualFlow from './AddVendorManualFlow.vue';
import {EXPENSE_ENTITY_LABEL_CONFIG} from '@/helpers/baseConfig';
import {expensePermissionsMixin} from '@/mixins';

export default {
   components: {
      AddVendorImportFlow,
      AddVendorManualFlow,
   },

   mixins: [expensePermissionsMixin],

   props: {
      expenseType: {
         type: String,
         required: true,
         validator: (value) => ['cloud', 'supplies', 'contractors', 'employees'].includes(value),
      },
   },

   data() {
      return {
         creatingProject: false,
         importModalState: 'uploadVendors',
         selected: this.getInitialSelected(),
      };
   },

   computed: {
      entityLabelPlural() {
         return (
            EXPENSE_ENTITY_LABEL_CONFIG[this.expenseType]?.plural ||
            EXPENSE_ENTITY_LABEL_CONFIG.supplies.plural
         );
      },

      canImportEmployees() {
         return this.expenseType !== 'employees' || this.hasPayrollAccess;
      },

      showFlowToggle() {
         if (this.expenseType === 'contractors' || !this.canImportEmployees) {
            return false;
         }
         return this.selected === 'manual' || this.importModalState === 'uploadVendors';
      },
   },

   methods: {
      // Vue initializes methods before computed/data, so the initial value can't read
      // canImportEmployees yet — read the store getters it derives from directly instead.
      getInitialSelected() {
         const canImportEmployees =
            this.expenseType !== 'employees' ||
            this.$store.getters.isRndig ||
            this.$store.getters['users/currentUserPermissionFlags'].payroll;
         return this.expenseType === 'contractors' || !canImportEmployees ? 'manual' : 'import';
      },

      resetState() {
         this.creatingProject = false;
         this.importModalState = 'uploadVendors';
         this.selected = this.getInitialSelected();
         if (this.$refs.importFlow) this.$refs.importFlow.resetState();
         if (this.$refs.manualFlow) this.$refs.manualFlow.resetState();
      },

      closeModal() {
         this.$refs.addVendorModal.hide();
         this.$emit('afterClose');
      },

      hidden() {
         this.resetState();
      },

      async changeState() {
         await Promise.all([
            this.$store.dispatch(`${this.expenseType}/loadData`, {
               companyId: this.$companyId,
            }),
            this.$store.dispatch(`${this.expenseType}/loadAllAssignmentsByCompanyId`, {
               companyId: this.$companyId,
            }),
         ]);
         this.$emit('afterClose');
      },
   },
};
</script>

<style lang="scss" scoped>
::v-deep .modal-dialog {
   max-width: 80%;
}

.full-width-line {
   margin-left: -2.5rem; /* same as modal content padding */
   margin-right: -2.5rem;
   border: none;
   border-top: 1px solid #ccc;
   margin-top: 0;
}

.footer-full-width-line {
   margin-left: -2.5rem; /* same as modal content padding */
   margin-right: -2.5rem;
   border: none;
   border-top: 1px solid #ccc;
   margin-bottom: 0;
}

.toggle-switch {
   border-radius: 0.75rem;
   font-weight: 500;
   padding: 5px;
   background-color: #eeeeee;
}

.my-custom-ok-btn {
   padding: 5px 30px;
   font-size: 14px !important;
   background-color: #1660a9 !important;
   border: 1px solid #1660a9;
   &:hover {
      background-color: #1660a9 !important;
      border: 1px solid #1660a9;
   }
}

::v-deep .modal-content {
   max-height: 93vh;
   overflow-y: auto;
}

::v-deep .modal-header {
   border-bottom: none !important;
   padding-bottom: 0 !important;
}

::v-deep .modal-footer {
   border-top: none !important;
}

::v-deep .modal-dialog {
   @media (max-width: 992px) {
      max-width: 90%;
   }
}
</style>
